/**
 * RFM Analytics Service
 *
 * API service layer for RFM (Recency, Frequency, Monetary) customer segmentation.
 * Integrates with useAuthenticatedApi hook for automatic authentication.
 * Admin/SuperAdmin access only.
 */

import type { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import type {
  RFMAnalysisResponse,
  SegmentCustomersData,
  RFMSegment
} from "@/shared/types/rfm-analytics";

/**
 * RFM Analytics API Service
 * Uses authenticated API client for all requests
 */
export class RFMAnalyticsService {
  constructor(private api: ReturnType<typeof useAuthenticatedApi>) {}

  /**
   * Get complete RFM analysis with all 11 segments
   * @param params - Optional period, limit, and preview parameters
   * @returns Complete RFM analysis response with segments and top customers
   */
  async getRFMAnalysis(params?: {
    period?: string; // YYYY-MM format
    limit?: number; // Max customers per segment (default 100, max 1000)
    previewAs?: string; // Admin preview mode: view as specific salesperson
  }): Promise<RFMAnalysisResponse> {
    const searchParams = new URLSearchParams();

    if (params?.period) {
      searchParams.set("period", params.period);
    }

    if (params?.limit !== undefined) {
      searchParams.set("limit", params.limit.toString());
    }

    if (params?.previewAs) {
      searchParams.set("previewAs", params.previewAs);
    }

    const url = `/api/rfm/analysis${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to fetch RFM analysis");
    }

    const result = await response.json() as { data: RFMAnalysisResponse };
    return result.data;
  }

  /**
   * Get paginated customer list for a specific RFM segment
   * @param segmentName - One of the 11 RFM segments
   * @param params - Optional period, pagination, and preview parameters
   * @returns Paginated customer list with RFM scores
   */
  async getSegmentCustomers(
    segmentName: RFMSegment,
    params?: {
      period?: string; // YYYY-MM format
      offset?: number; // Pagination offset (default 0)
      limit?: number; // Pagination limit (default 50, max 500)
      previewAs?: string; // Admin preview mode: view as specific salesperson
    }
  ): Promise<SegmentCustomersData> {
    const searchParams = new URLSearchParams();

    if (params?.period) {
      searchParams.set("period", params.period);
    }

    if (params?.offset !== undefined) {
      searchParams.set("offset", params.offset.toString());
    }

    if (params?.limit !== undefined) {
      searchParams.set("limit", params.limit.toString());
    }

    if (params?.previewAs) {
      searchParams.set("previewAs", params.previewAs);
    }

    // URL-encode segment name (handles spaces and apostrophes)
    const encodedSegment = encodeURIComponent(segmentName);
    const url = `/api/rfm/segment/${encodedSegment}${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to fetch segment customers");
    }

    const result = await response.json() as { data: SegmentCustomersData };
    return result.data;
  }

  /**
   * Export RFM segment data to CSV format
   * @param segmentName - Segment to export
   * @param params - Optional period parameter
   * @returns CSV string with customer data
   */
  async exportSegmentToCSV(
    segmentName: RFMSegment,
    params?: {
      period?: string;
    }
  ): Promise<string> {
    // Fetch all customers in segment (use max limit)
    const data = await this.getSegmentCustomers(segmentName, {
      period: params?.period,
      offset: 0,
      limit: 1000 // Max limit to get all customers
    });

    // Convert to CSV format
    const headers = [
      'Customer ID',
      'Customer Name',
      'R Score',
      'F Score',
      'M Score',
      'Composite Score',
      'Segment',
      'Total Revenue',
      'Total Purchases',
      'Last Purchase Date',
      'Days Since Last Purchase',
      'Average Order Value'
    ];

    const rows = data.customers.map(customer => [
      customer.customerId,
      customer.customerName,
      customer.rfmScore.recencyScore,
      customer.rfmScore.frequencyScore,
      customer.rfmScore.monetaryScore,
      customer.rfmScore.compositeScore,
      customer.segment,
      customer.metrics.totalRevenue.toFixed(2),
      customer.metrics.totalPurchases,
      customer.metrics.lastPurchaseDate,
      customer.metrics.daysSinceLastPurchase,
      customer.metrics.averageOrderValue.toFixed(2)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csvContent;
  }

  /**
   * Export complete RFM analysis summary to CSV format
   * @param params - Optional period parameter
   * @returns CSV string with segment summaries
   */
  async exportAnalysisToCSV(params?: {
    period?: string;
  }): Promise<string> {
    const analysis = await this.getRFMAnalysis({
      period: params?.period,
      limit: 0 // We only need segment summaries, not customers
    });

    // Convert to CSV format
    const headers = [
      'Segment',
      'Customer Count',
      'Percentage',
      'Total Revenue',
      'Average Revenue',
      'Priority'
    ];

    const rows = analysis.segments.map(segment => [
      segment.segment,
      segment.customerCount,
      segment.percentage.toFixed(1),
      segment.totalRevenue.toFixed(2),
      segment.averageRevenue.toFixed(2),
      // Get priority from segment definitions
      segment.customerCount > 0 ? 'Active' : 'Empty'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csvContent;
  }
}

